(() => {
  'use strict';

  if (window.__WHITEHOLE_V67__) return;
  window.__WHITEHOLE_V67__ = true;

  const q = (selector, root = document) => root.querySelector(selector);
  const qa = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const reduceMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const readyMotion = () => {
    if (reduceMotion()) return;
    window.setTimeout(() => document.body.classList.add('wh67-motion-ready'), 1600);
  };

  const initSuctionCanvas = () => {
    const canvas = q('[data-wh67-suction]');
    const hero = canvas?.closest('[data-wh67-hero]');
    if (!canvas || !hero || reduceMotion()) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    let width = 0;
    let height = 0;
    let dpr = 1;
    let centerX = 0;
    let centerY = 0;
    let maxRadius = 0;
    let raf = 0;
    let last = performance.now();

    const colors = [
      [255,255,255],
      [127,221,255],
      [124,151,255],
      [207,160,255]
    ];

    const makeParticle = (fresh = false) => {
      const radius = fresh ? Math.random() * maxRadius : maxRadius * (.45 + Math.random() * .68);
      return {
        angle: Math.random() * Math.PI * 2,
        radius,
        speed: .00055 + Math.random() * .00135,
        inward: .014 + Math.random() * .032,
        depth: Math.random(),
        size: .55 + Math.random() * 2.2,
        alpha: .24 + Math.random() * .76,
        color: colors[Math.floor(Math.random() * colors.length)],
        trail: .5 + Math.random() * 1.7
      };
    };

    let particles = [];

    const resize = () => {
      const rect = hero.getBoundingClientRect();
      width = Math.max(1, Math.round(rect.width));
      height = Math.max(1, Math.round(rect.height));
      dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const mobile = width < 750;
      const tablet = width < 990;
      centerX = width * (mobile ? .705 : tablet ? .69 : .645);
      centerY = height * (mobile ? .30 : tablet ? .39 : .52);
      maxRadius = Math.max(width, height) * (mobile ? .48 : .56);
      const count = mobile ? 38 : tablet ? 64 : 112;
      particles = Array.from({ length: count }, () => makeParticle());
    };

    const resetParticle = (particle) => {
      const fresh = makeParticle(true);
      Object.assign(particle, fresh);
      particle.radius = maxRadius * (.74 + Math.random() * .34);
    };

    const draw = (now) => {
      const dt = Math.min(40, now - last);
      last = now;
      ctx.clearRect(0, 0, width, height);
      ctx.globalCompositeOperation = 'lighter';

      for (const p of particles) {
        p.angle += p.speed * dt * (1.2 + (1 - p.depth) * 1.6);
        p.radius -= p.inward * dt * (1 + (1 - p.depth) * 1.8);
        if (p.radius < 8) resetParticle(p);

        const elliptical = .58 + p.depth * .18;
        const x = centerX + Math.cos(p.angle) * p.radius;
        const y = centerY + Math.sin(p.angle) * p.radius * elliptical;
        const previousAngle = p.angle - .022 * p.trail * (1 + (maxRadius - p.radius) / maxRadius);
        const previousRadius = p.radius + 3 + p.trail * 5;
        const px = centerX + Math.cos(previousAngle) * previousRadius;
        const py = centerY + Math.sin(previousAngle) * previousRadius * elliptical;

        const proximity = 1 - Math.min(1, p.radius / maxRadius);
        const alpha = Math.min(1, p.alpha * (.5 + proximity * .75));
        const [r,g,b] = p.color;
        const gradient = ctx.createLinearGradient(px, py, x, y);
        gradient.addColorStop(0, `rgba(${r},${g},${b},0)`);
        gradient.addColorStop(1, `rgba(${r},${g},${b},${alpha})`);
        ctx.strokeStyle = gradient;
        ctx.lineWidth = Math.max(.45, p.size * (.5 + proximity));
        ctx.beginPath();
        ctx.moveTo(px, py);
        ctx.lineTo(x, y);
        ctx.stroke();

        if (p.size > 1.2) {
          ctx.fillStyle = `rgba(${r},${g},${b},${Math.min(1, alpha * 1.05)})`;
          ctx.beginPath();
          ctx.arc(x, y, Math.max(.45, p.size * (.35 + proximity * .35)), 0, Math.PI * 2);
          ctx.fill();
        }
      }

      ctx.globalCompositeOperation = 'source-over';
      raf = requestAnimationFrame(draw);
    };

    resize();
    window.addEventListener('resize', resize, { passive: true });
    let canvasVisible = true;
    const stop = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    };
    const start = () => {
      if (raf || document.hidden || !canvasVisible) return;
      last = performance.now();
      raf = requestAnimationFrame(draw);
    };
    window.setTimeout(start, 1400);

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        canvasVisible = entries.some((entry) => entry.isIntersecting);
        if (canvasVisible) start();
        else stop();
      }, { rootMargin: '160px 0px' });
      observer.observe(hero);
    }

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stop();
      else start();
    });
  };


  const wh76CutoutCache = new Map();

  const wh76ColorDistance = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);

  const makeHeroCutout = async (sourceUrl) => {
    if (!sourceUrl) return sourceUrl;
    if (wh76CutoutCache.has(sourceUrl)) return wh76CutoutCache.get(sourceUrl);

    const task = new Promise((resolve) => {
      const sourceImage = new Image();
      sourceImage.crossOrigin = 'anonymous';
      sourceImage.decoding = 'async';
      sourceImage.onload = () => {
        try {
          const maxSize = 180;
          const scale = Math.min(1, maxSize / Math.max(sourceImage.naturalWidth || 1, sourceImage.naturalHeight || 1));
          const width = Math.max(1, Math.round(sourceImage.naturalWidth * scale));
          const height = Math.max(1, Math.round(sourceImage.naturalHeight * scale));
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const context = canvas.getContext('2d', { willReadFrequently: true });
          if (!context) return resolve(null);
          context.drawImage(sourceImage, 0, 0, width, height);
          const pixels = context.getImageData(0, 0, width, height);
          const data = pixels.data;

          const border = [];
          const take = (x, y) => {
            const idx = (y * width + x) * 4;
            border.push([data[idx], data[idx + 1], data[idx + 2]]);
          };
          for (let x = 0; x < width; x += 1) {
            take(x, 0);
            take(x, height - 1);
          }
          for (let y = 1; y < height - 1; y += 1) {
            take(0, y);
            take(width - 1, y);
          }

          const average = border.reduce((acc, rgb) => [acc[0] + rgb[0], acc[1] + rgb[1], acc[2] + rgb[2]], [0, 0, 0]).map((value) => value / Math.max(1, border.length));
          const spread = border.reduce((sum, rgb) => sum + wh76ColorDistance(rgb, average), 0) / Math.max(1, border.length);
          const tolerance = Math.max(42, Math.min(118, spread * 1.85 + 32));

          const visited = new Uint8Array(width * height);
          const queue = new Int32Array(width * height);
          let head = 0;
          let tail = 0;
          const enqueue = (x, y) => {
            if (x < 0 || y < 0 || x >= width || y >= height) return;
            const pos = y * width + x;
            if (visited[pos]) return;
            visited[pos] = 1;
            queue[tail++] = pos;
          };

          for (let x = 0; x < width; x += 1) {
            enqueue(x, 0);
            enqueue(x, height - 1);
          }
          for (let y = 1; y < height - 1; y += 1) {
            enqueue(0, y);
            enqueue(width - 1, y);
          }

          let removed = 0;
          while (head < tail) {
            const pos = queue[head++];
            const x = pos % width;
            const y = Math.floor(pos / width);
            const idx = pos * 4;
            const rgba = [data[idx], data[idx + 1], data[idx + 2]];
            const alpha = data[idx + 3];
            if (!alpha) continue;
            const nearAverage = wh76ColorDistance(rgba, average) <= tolerance;
            const nearAnyBorder = border.length > 24 && (wh76ColorDistance(rgba, border[pos % border.length]) <= tolerance * 1.08);
            if (!(nearAverage || nearAnyBorder)) continue;
            data[idx + 3] = 0;
            removed += 1;
            enqueue(x + 1, y);
            enqueue(x - 1, y);
            enqueue(x, y + 1);
            enqueue(x, y - 1);
          }

          const removedRatio = removed / Math.max(1, width * height);
          if (removedRatio < 0.02 || removedRatio > 0.96) return resolve(null);

          let minX = width, minY = height, maxX = -1, maxY = -1;
          for (let y = 0; y < height; y += 1) {
            for (let x = 0; x < width; x += 1) {
              const idx = (y * width + x) * 4;
              if (data[idx + 3] > 10) {
                if (x < minX) minX = x;
                if (y < minY) minY = y;
                if (x > maxX) maxX = x;
                if (y > maxY) maxY = y;
              }
            }
          }

          if (maxX < minX || maxY < minY) return resolve(null);

          context.putImageData(pixels, 0, 0);

          const pad = 10;
          minX = Math.max(0, minX - pad);
          minY = Math.max(0, minY - pad);
          maxX = Math.min(width - 1, maxX + pad);
          maxY = Math.min(height - 1, maxY + pad);
          const outW = Math.max(1, maxX - minX + 1);
          const outH = Math.max(1, maxY - minY + 1);
          const outCanvas = document.createElement('canvas');
          outCanvas.width = outW;
          outCanvas.height = outH;
          const outContext = outCanvas.getContext('2d');
          if (!outContext) return resolve(null);
          outContext.drawImage(canvas, minX, minY, outW, outH, 0, 0, outW, outH);
          resolve(outCanvas.toDataURL('image/webp', 0.92));
        } catch (_) {
          resolve(sourceUrl);
        }
      };
      sourceImage.onerror = () => resolve(null);
      sourceImage.src = sourceUrl;
    });

    wh76CutoutCache.set(sourceUrl, task);
    return task;
  };

  const initHeroProductStream = () => {
    const container = q('[data-wh71-hero-products]');
    if (!container || container.dataset.wh86Ready === 'true') return;
    container.dataset.wh86Ready = 'true';

    const allSlots = qa('[data-wh71-product-slot]', container);
    if (!allSlots.length) return;

    const mobileHero = window.matchMedia('(max-width: 749px)').matches;
    const activeLimit = mobileHero ? 3 : 6;
    const slots = allSlots.slice(0, activeLimit);
    allSlots.slice(activeLimit).forEach((slot) => {
      slot.hidden = true;
      slot.style.display = 'none';
    });

    const setSlotHidden = (slot, hidden) => {
      const image = q('[data-wh71-product-image]', slot);
      slot.classList.toggle('is-cutout-loading', hidden);
      slot.setAttribute('aria-hidden', hidden ? 'true' : 'false');
      slot.tabIndex = hidden ? -1 : 0;
      if (image) image.style.opacity = hidden ? '0' : '1';
    };

    slots.forEach((slot) => setSlotHidden(slot, true));

    const chooseProductImage = (product) => {
      const images = Array.isArray(product?.images) ? product.images : [];
      if (!images.length) return null;
      const bad = /(size|dimension|measurement|measure|chart|manual|specification|parameter|instruction)/i;
      return images.find((item) => !bad.test(`${item?.alt || ''} ${item?.src || ''}`)) || images[0];
    };

    const applyProduct = async (slot, product) => {
      if (!slot || slot.dataset.wh86Loading === 'true') return false;
      const image = q('[data-wh71-product-image]', slot);
      const productImage = chooseProductImage(product);
      if (!image || !productImage?.src) return false;

      slot.dataset.wh86Loading = 'true';
      const requestId = `${product.id || product.handle}-${Date.now()}-${Math.random()}`;
      slot.dataset.wh86Request = requestId;
      setSlotHidden(slot, true);

      const cutoutUrl = await makeHeroCutout(productImage.src);
      if (slot.dataset.wh86Request !== requestId) {
        slot.dataset.wh86Loading = 'false';
        return false;
      }
      if (!cutoutUrl) {
        slot.dataset.wh86Loading = 'false';
        return false;
      }

      image.src = cutoutUrl;
      image.alt = product.title || 'Store product';
      slot.href = `${window.Shopify?.routes?.root || '/'}products/${product.handle}`;
      slot.dataset.wh86Loading = 'false';
      setSlotHidden(slot, false);
      return true;
    };

    const start = async () => {
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}products.json?limit=250`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          cache: 'force-cache'
        });
        if (!response.ok) throw new Error('Product stream request failed');
        const payload = await response.json();
        const seen = new Set();
        const products = (payload.products || []).filter((product) => {
          const productImage = chooseProductImage(product);
          const key = product?.id || product?.handle;
          if (!key || !productImage?.src || seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        if (!products.length) return;

        let cursor = 0;
        const nextSuccessfulProduct = async (slot) => {
          const attempts = Math.min(products.length, 12);
          for (let attempt = 0; attempt < attempts; attempt += 1) {
            const product = products[cursor % products.length];
            cursor += 1;
            // eslint-disable-next-line no-await-in-loop
            if (await applyProduct(slot, product)) return true;
          }
          setSlotHidden(slot, true);
          return false;
        };

        for (const slot of slots) {
          // eslint-disable-next-line no-await-in-loop
          await nextSuccessfulProduct(slot);
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => window.setTimeout(resolve, 70));
        }

        slots.forEach((slot) => {
          slot.addEventListener('animationiteration', () => {
            if (slot.dataset.wh86Loading === 'true') return;
            nextSuccessfulProduct(slot);
          });
        });
      } catch (_) {
        // Never show unrelated fallback products. Keep the hero stream empty if live store products are unavailable.
        slots.forEach((slot) => setSlotHidden(slot, true));
      }
    };

    const launch = () => {
      if ('requestIdleCallback' in window) window.requestIdleCallback(start, { timeout: 1800 });
      else window.setTimeout(start, 500);
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        const visible = entries.some((entry) => entry.isIntersecting);
        container.classList.toggle('wh-hero-paused', !visible);
        if (visible && container.dataset.whPerfStarted !== 'true') {
          container.dataset.whPerfStarted = 'true';
          launch();
        }
      }, { rootMargin: '120px 0px' });
      observer.observe(container);
    } else {
      launch();
    }
  };

  const initCategoryCarousel = () => {
    qa('[data-wh67-categories]').forEach((section) => {
      if (section.dataset.wh67Ready === 'true') return;
      const rail = q('.wh67-category-rail', section);
      const prev = q('[data-wh67-category-prev]', section);
      const next = q('[data-wh67-category-next]', section);
      if (!rail || !prev || !next) return;
      section.dataset.wh67Ready = 'true';

      const step = () => Math.max(240, rail.clientWidth * .82);
      const update = () => {
        const max = Math.max(0, rail.scrollWidth - rail.clientWidth - 3);
        prev.disabled = rail.scrollLeft <= 3;
        next.disabled = rail.scrollLeft >= max;
      };
      prev.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: reduceMotion() ? 'auto' : 'smooth' }));
      next.addEventListener('click', () => rail.scrollBy({ left: step(), behavior: reduceMotion() ? 'auto' : 'smooth' }));

      // Native horizontal swipe remains in charge on phones and tablets.
      // Suppress card navigation only when the finger actually moved the rail.
      let touchStartX = 0;
      let touchStartY = 0;
      let touchMoved = false;
      let suppressClickUntil = 0;
      rail.addEventListener('touchstart', (event) => {
        const touch = event.touches[0];
        if (!touch) return;
        touchStartX = touch.clientX;
        touchStartY = touch.clientY;
        touchMoved = false;
      }, { passive: true });
      rail.addEventListener('touchmove', (event) => {
        const touch = event.touches[0];
        if (!touch) return;
        const dx = Math.abs(touch.clientX - touchStartX);
        const dy = Math.abs(touch.clientY - touchStartY);
        if (dx > 8 && dx > dy) touchMoved = true;
      }, { passive: true });
      rail.addEventListener('touchend', () => {
        if (touchMoved) suppressClickUntil = Date.now() + 350;
      }, { passive: true });
      rail.addEventListener('click', (event) => {
        if (Date.now() < suppressClickUntil) {
          event.preventDefault();
          event.stopPropagation();
        }
      }, true);

      rail.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
      update();
    });
  };

  const initProductRibbons = () => {
    qa('[data-wh67-ribbon]').forEach((section) => {
      if (section.dataset.wh67Ready === 'true') return;
      const viewport = q('[data-wh67-ribbon-viewport]', section);
      const track = q('[data-wh67-ribbon-track]', section);
      const firstGroup = q('[data-wh67-ribbon-group]', section);
      if (!viewport || !track || !firstGroup) return;
      section.dataset.wh67Ready = 'true';

      const autoplay = section.dataset.autoplay === 'true' && !reduceMotion();
      const toggle = q('[data-wh67-ribbon-toggle]', section);
      const pauseIcon = q('[data-wh67-pause-icon]', section);
      const playIcon = q('[data-wh67-play-icon]', section);
      const label = q('[data-wh67-toggle-label]', section);
      const prev = q('[data-wh67-ribbon-prev]', section);
      const next = q('[data-wh67-ribbon-next]', section);
      let animation = null;
      let userPaused = !autoplay;
      let hoverPaused = false;
      let resumeTimer = 0;

      const durationSeconds = Math.max(18, Number.parseFloat(getComputedStyle(section).getPropertyValue('--wh67-ribbon-duration')) || 42);
      const syncDesktopCardWidth = () => {
        if (window.innerWidth < 990) {
          section.style.removeProperty('--wh160-ribbon-card-width');
          return;
        }
        const gap = Math.max(0, Number.parseFloat(getComputedStyle(section).getPropertyValue('--wh67-ribbon-gap')) || 14);
        const width = Math.max(210, (viewport.clientWidth - gap * 4) / 5);
        section.style.setProperty('--wh160-ribbon-card-width', `${width}px`);
      };
      const groupWidth = () => Math.max(1, firstGroup.getBoundingClientRect().width);

      const updateToggle = () => {
        if (!toggle) return;
        toggle.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
        toggle.setAttribute('aria-label', userPaused ? 'Play product ribbon' : 'Pause product ribbon');
        if (pauseIcon) pauseIcon.hidden = userPaused;
        if (playIcon) playIcon.hidden = !userPaused;
        if (label) label.textContent = userPaused ? 'Play' : 'Pause';
      };

      const createAnimation = () => {
        syncDesktopCardWidth();
        const oldTime = animation?.currentTime || 0;
        animation?.cancel();
        if (!autoplay) return;
        const width = groupWidth();
        animation = track.animate(
          [{ transform: 'translate3d(0,0,0)' }, { transform: `translate3d(-${width}px,0,0)` }],
          { duration: durationSeconds * 1000, iterations: Infinity, easing: 'linear' }
        );
        animation.currentTime = oldTime;
        if (userPaused || hoverPaused || document.hidden) animation.pause();
      };

      const pauseForInteraction = (milliseconds = 1700) => {
        if (!animation || userPaused) return;
        animation.pause();
        clearTimeout(resumeTimer);
        resumeTimer = window.setTimeout(() => {
          if (!userPaused && !hoverPaused && !document.hidden) animation.play();
        }, milliseconds);
      };

      const shiftBy = (direction) => {
        if (!animation) return;
        pauseForInteraction(1500);
        const width = groupWidth();
        const duration = durationSeconds * 1000;
        const current = Number(animation.currentTime || 0);
        const delta = duration * Math.min(.8, Math.max(.12, viewport.clientWidth / width * .82));
        const nextTime = ((current + direction * delta) % duration + duration) % duration;
        animation.currentTime = nextTime;
      };

      toggle?.addEventListener('click', () => {
        userPaused = !userPaused;
        if (animation) userPaused ? animation.pause() : animation.play();
        updateToggle();
      });
      prev?.addEventListener('click', () => shiftBy(-1));
      next?.addEventListener('click', () => shiftBy(1));
      viewport.addEventListener('pointerenter', () => { hoverPaused = true; animation?.pause(); });
      viewport.addEventListener('pointerleave', () => { hoverPaused = false; if (!userPaused && !document.hidden) animation?.play(); });
      viewport.addEventListener('touchstart', () => pauseForInteraction(2200), { passive: true });
      viewport.addEventListener('wheel', () => pauseForInteraction(1600), { passive: true });
      document.addEventListener('visibilitychange', () => {
        if (!animation) return;
        if (document.hidden) animation.pause();
        else if (!userPaused && !hoverPaused) animation.play();
      });

      updateToggle();
      syncDesktopCardWidth();
      window.setTimeout(createAnimation, 2300);
      let resizeTimer = 0;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(createAnimation, 180);
      }, { passive: true });
    });
  };

  const initProductCardClicks = () => {
    qa('.product-card-wrapper').forEach((wrapper) => {
      if (wrapper.dataset.whFullCardBound === 'true') return;
      const destination = q('.card__heading a, .full-unstyled-link[href*="/products/"]', wrapper);
      if (!destination) return;
      wrapper.dataset.whFullCardBound = 'true';
      wrapper.setAttribute('role', 'link');
      wrapper.tabIndex = 0;
      const navigate = (event) => {
        if (event.target.closest('a,button,input,select,textarea,label,form,details,summary')) return;
        window.location.assign(destination.href);
      };
      wrapper.addEventListener('click', navigate);
      wrapper.addEventListener('keydown', (event) => {
        if ((event.key === 'Enter' || event.key === ' ') && !event.target.closest('a,button,input,select,textarea')) {
          event.preventDefault();
          window.location.assign(destination.href);
        }
      });
    });
  };

  const initCleanProductGallery = () => {
    qa('[data-wh-product-gallery]').forEach((gallery) => {
      if (gallery.dataset.whCleanGalleryReady === 'true') return;
      gallery.dataset.whCleanGalleryReady = 'true';
      const list = q('.product__media-list', gallery);
      if (!list) return;
      const items = qa('.product__media-item', list);
      if (items.length < 2) return;
      const badPattern = /(size|dimension|measurement|measure|chart|manual|specification|parameter)/i;
      const current = items[0];
      const currentImage = q('img', current);
      const currentSignature = `${currentImage?.alt || ''} ${currentImage?.currentSrc || currentImage?.src || ''}`;
      if (!badPattern.test(currentSignature)) return;
      const cleanItem = items.find((item) => {
        const image = q('img', item);
        if (!image) return false;
        const signature = `${image.alt || ''} ${image.currentSrc || image.src || ''}`;
        return !badPattern.test(signature);
      });
      if (!cleanItem || cleanItem === current) return;
      current.classList.remove('is-active');
      cleanItem.classList.add('is-active');
      list.prepend(cleanItem);
      const targetId = cleanItem.dataset.mediaId;
      if (targetId) {
        qa('.thumbnail-list__item', gallery).forEach((thumb) => {
          const button = q('[data-target]', thumb) || thumb;
          const matches = thumb.dataset.target === targetId || button.dataset?.target === targetId;
          q('.thumbnail', thumb)?.toggleAttribute('aria-current', matches);
        });
      }
    });
  };

  const initProductImageRecovery = () => {
    qa('[data-wh-product-image]').forEach((image) => {
      if (image.dataset.whImageRecoveryReady === 'true') return;
      image.dataset.whImageRecoveryReady = 'true';

      const showImage = () => {
        image.style.opacity = '1';
        image.style.visibility = 'visible';
      };

      const recoverImage = () => {
        const fallback = image.dataset.whFallbackSrc;
        if (!fallback || image.dataset.whFallbackApplied === 'true') {
          showImage();
          return;
        }
        image.dataset.whFallbackApplied = 'true';
        image.classList.add('is-wh-image-fallback');
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.src = fallback;
        showImage();
      };

      image.addEventListener('load', showImage);
      image.addEventListener('error', recoverImage);

      if (image.complete) {
        if (image.naturalWidth > 1 && image.naturalHeight > 1) showImage();
        else recoverImage();
      }
    });
  };

  const initReviewDialogs = () => {
    qa('[data-wh67-reviews]').forEach((section) => {
      if (section.dataset.wh67Ready === 'true') return;
      const dialog = q('[data-wh67-review-dialog]', section);
      const open = q('[data-wh67-review-open]', section);
      const closeButtons = qa('[data-wh67-review-close]', section);
      if (!dialog || !open) return;
      section.dataset.wh67Ready = 'true';

      const setOpen = (value) => {
        dialog.hidden = !value;
        document.body.classList.toggle('wh67-modal-open', value);
        if (value) q('input,button,textarea', dialog)?.focus();
        else open.focus();
      };
      open.addEventListener('click', () => setOpen(true));
      closeButtons.forEach((button) => button.addEventListener('click', () => setOpen(false)));
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !dialog.hidden) setOpen(false);
      });
    });
  };

  const init = () => {
    readyMotion();
    initSuctionCanvas();
    initHeroProductStream();
    initCategoryCarousel();
    initProductRibbons();
    initProductCardClicks();
    initProductImageRecovery();
    initCleanProductGallery();
    initReviewDialogs();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', () => window.setTimeout(init, 80));
})();
